/**
 * Lista de equipamentos de incêndio (#363, NR 23): a faixa "Precisam de atenção" por motivo,
 * filtros em linha e a situação de cada equipamento. Os rótulos em português vêm todos de
 * `GET /fire-equipments/types`; os dias, do servidor.
 *
 * Alvo visual: docs/superpowers/specs/2026-10-15-363-incendio-nr23-mockup.html (aba 1).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Alert, Box, Button, Link, Skeleton, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import {
  DataTable,
  InlineFilterBar,
  InlineSelect,
  PageShell,
  RouteActionButton,
  type DataTableColumn,
} from '../../components';
import { ActionButton } from '../../components/common/ActionButton';
import { ValidityChip } from '../../components/common/ValidityChip';
import type { ActiveChip } from '../../hooks/useFilterState';
import { pode } from '../../authorization/pode';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useControle } from '../../authorization/useVeredito';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { fireEquipmentService } from '../../services/fireEquipmentService';
import type {
  FireDueField,
  FireEquipment,
  FireEquipmentDetail,
  FireEquipmentFilters,
  FireEquipmentSummary,
  FireEquipmentTypes,
  FireSituationReason,
} from '../../types/fireEquipment';
import InspectionDialog from './components/InspectionDialog';
import { dueState } from './dueState';
import { capacityText, equipmentKind, equipmentSummary } from './equipmentText';
import { formatIsoDate, saoPauloDay, todayIso } from './fireDates';

type SituationFilter = FireSituationReason | 'FORA_DE_USO';

/** Os valores que a API aceita em `situation`; qualquer outro vindo da URL é descartado. */
const SITUATION_FILTERS: readonly SituationFilter[] = [
  'VENCIDO',
  'COM_PENDENCIA',
  'A_VENCER',
  'SEM_INSPECAO',
  'FORA_DE_USO',
];

/** A ordem da faixa de atenção e o contador do resumo que cada botão lê. */
const ATTENTION_BUTTONS: readonly { reason: FireSituationReason; count: keyof FireEquipmentSummary }[] = [
  { reason: 'VENCIDO', count: 'vencidos' },
  { reason: 'A_VENCER', count: 'aVencer' },
  { reason: 'COM_PENDENCIA', count: 'comPendencia' },
  { reason: 'SEM_INSPECAO', count: 'semInspecao' },
];

const TOUCH_TARGET = 44;

function pluralDays(count: number): string {
  return `${count} ${count === 1 ? 'dia' : 'dias'}`;
}

/** O dia do cadastro comparado ao de hoje, os dois no calendário de São Paulo. */
function createdToday(createdAt: string): boolean {
  return saoPauloDay(createdAt) === todayIso();
}

/** A frase pequena sob a data: só o que o servidor já calculou, nunca uma conta do navegador. */
function dueHint(equipment: FireEquipment, field: FireDueField, days: number): string | undefined {
  const state = dueState(equipment, field);
  if (state === 'VENCIDO') return `venceu há ${pluralDays(-days)}`;
  if (state === 'PRIMEIRA_INSPECAO') {
    return days === 0 ? '1ª inspeção, hoje' : `1ª inspeção, em ${pluralDays(days)}`;
  }
  if (state === 'EM_DIA') return undefined;
  if (days === 0) return 'vence hoje';
  return field === 'nextInspection' ? `em ${pluralDays(days)}` : `vence em ${pluralDays(days)}`;
}

const SmallText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" color="text.secondary">
    {children}
  </Typography>
);

const DueCell: React.FC<{ equipment: FireEquipment; field: FireDueField }> = ({ equipment, field }) => {
  const due = equipment.dues[field];
  // Em fora de uso a data guardada pode estar no passado e não significa atraso.
  if (!due || equipment.status === 'DECOMMISSIONED') return <>—</>;

  const hint = due.days === null ? undefined : dueHint(equipment, field, due.days);
  return (
    <Box>
      <Typography variant="body1">{formatIsoDate(due.date)}</Typography>
      {hint && <SmallText>{hint}</SmallText>}
    </Box>
  );
};

const SituationCell: React.FC<{ equipment: FireEquipment; tipos: FireEquipmentTypes }> = ({ equipment, tipos }) => {
  const { code, motivos } = equipment.situation;
  const comingUp = motivos.find((motivo) => motivo.reason === 'A_VENCER');
  const hasPendency = motivos.some((motivo) => motivo.reason === 'COM_PENDENCIA');
  const label = tipos.labels.situation[code];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
      {code === 'A_VENCER' && comingUp?.days !== undefined ? (
        <ValidityChip situation={code} days={comingUp.days} />
      ) : (
        <ValidityChip situation={code} label={label} />
      )}
      {hasPendency && <SmallText>última inspeção não conforme</SmallText>}
      {code === 'SEM_INSPECAO' && createdToday(equipment.createdAt) && <SmallText>cadastrado hoje</SmallText>}
    </Box>
  );
};

interface RowActions {
  opening: number | null;
  reactivating: number | null;
  onRegister: (equipment: FireEquipment) => void;
  onReactivate: (equipment: FireEquipment) => void;
}

const buildColumns = (tipos: FireEquipmentTypes, actions: RowActions): DataTableColumn<FireEquipment>[] => [
  {
    id: 'identifier',
    label: 'Equipamento',
    primary: true,
    render: (equipment) => (
      <Box>
        <Link
          component={RouterLink}
          to={`/fire-equipments/${equipment.id}`}
          variant="body1"
          fontWeight="bold"
          underline="hover"
          color="inherit"
          // A linha toda também abre a ficha; sem isto o clique no link navegaria duas vezes.
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
          {equipment.identifier}
        </Link>
        <SmallText>{equipmentKind(equipment, tipos)}</SmallText>
      </Box>
    ),
  },
  {
    id: 'location',
    label: 'Onde fica',
    render: (equipment) => (
      <Box>
        <Typography variant="body1">{equipment.location}</Typography>
        {equipment.departmentName && <SmallText>{equipment.departmentName}</SmallText>}
      </Box>
    ),
  },
  { id: 'capacity', label: 'Capacidade', hideOnMobile: true, render: (equipment) => capacityText(equipment, tipos) ?? '—' },
  { id: 'charge', label: 'Carga vence', render: (equipment) => <DueCell equipment={equipment} field="charge" /> },
  {
    id: 'hydrostatic',
    label: 'Teste hidrostático vence',
    render: (equipment) => <DueCell equipment={equipment} field="hydrostatic" />,
  },
  {
    id: 'nextInspection',
    label: 'Próxima inspeção',
    render: (equipment) => <DueCell equipment={equipment} field="nextInspection" />,
  },
  {
    id: 'situation',
    label: 'Situação',
    render: (equipment) => <SituationCell equipment={equipment} tipos={tipos} />,
  },
  {
    id: 'actions',
    label: 'Ações',
    render: (equipment) => (
      // O clique na ação é da ação: sem isto ele sobe até a linha e abre a ficha por cima.
      <Box onClick={(event) => event.stopPropagation()}>
        {equipment.status === 'DECOMMISSIONED' ? (
          <ActionButton
            acao="fire_equipment.update"
            rotulo="Reativar"
            sx={{ minHeight: TOUCH_TARGET }}
            disabled={actions.reactivating === equipment.id}
            onClick={() => actions.onReactivate(equipment)}
          >
            Reativar
          </ActionButton>
        ) : (
          <ActionButton
            acao="fire_equipment.update"
            rotulo="Registrar inspeção"
            sx={{ minHeight: TOUCH_TARGET }}
            disabled={actions.opening === equipment.id}
            onClick={() => actions.onRegister(equipment)}
          >
            Registrar inspeção
          </ActionButton>
        )}
      </Box>
    ),
  },
];

/** Id vindo da URL: só dígitos valem; qualquer outra coisa é como se não estivesse lá. */
const onlyDigits = (value: string | null): string => (value && /^\d+$/.test(value) ? value : '');

const errorMessage = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

export const FireEquipmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { shouldShowCompanyField } = useCompanyContext();
  const criar = useControle('/fire-equipments/new', pode('fire_equipment.create'), 'Cadastrar o primeiro equipamento');

  const [searchParams, setSearchParams] = useSearchParams();
  const [tipos, setTipos] = useState<FireEquipmentTypes | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState<FireEquipment[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<FireEquipmentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [typesAttempt, setTypesAttempt] = useState(0);
  const [opening, setOpening] = useState<number | null>(null);
  const [reactivating, setReactivating] = useState<number | null>(null);
  const [inspecting, setInspecting] = useState<FireEquipmentDetail | null>(null);

  const companyId = onlyDigits(searchParams.get('companyId'));
  const departmentId = onlyDigits(searchParams.get('departmentId'));
  const situationParam = searchParams.get('situation');
  const situation = SITUATION_FILTERS.find((option) => option === situationParam) ?? '';
  const typeParam = searchParams.get('type');
  const type = tipos?.types.find((descriptor) => descriptor.type === typeParam)?.type ?? '';

  const loadCompanyOptions = useCallback(async (): Promise<readonly { value: string; label: string }[]> => {
    const companies = await companyService.getCompaniesForDropdown();
    return companies.map((company) => ({ value: String(company.id), label: company.name }));
  }, []);
  const companyList = useListaPermitida('company.read', 'Empresas', loadCompanyOptions, {
    habilitado: shouldShowCompanyField,
  });

  const loadDepartmentOptions = useCallback(async (): Promise<readonly { value: string; label: string }[]> => {
    const response = await departmentService.getDepartments({
      page: 1,
      limit: 1000,
      ...(companyId ? { companyId } : {}),
    });
    return response.data.map((department) => ({ value: String(department.id), label: department.name }));
  }, [companyId]);
  const departmentList = useListaPermitida('department.read', 'Setores', loadDepartmentOptions);

  useEffect(() => {
    let alive = true;
    fireEquipmentService
      .getTypes()
      .then((response) => alive && setTipos(response))
      .catch((err: unknown) => {
        if (!alive) return;
        setError(errorMessage(err, 'Erro ao carregar os tipos de equipamento'));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [typesAttempt]);

  useEffect(() => {
    if (!tipos) return;
    let alive = true;
    setSummaryError(null);
    fireEquipmentService
      .getSummary(companyId || undefined, departmentId || undefined)
      .then((response) => alive && setSummary(response))
      .catch((err: unknown) => {
        if (!alive) return;
        setSummary(null);
        setSummaryError(errorMessage(err, 'Erro ao carregar o resumo'));
      });
    return () => {
      alive = false;
    };
  }, [tipos, companyId, departmentId, reload]);

  useEffect(() => {
    if (!tipos) return;
    let alive = true;
    const filters: FireEquipmentFilters = { page: page + 1, limit: rowsPerPage };
    if (companyId) filters.companyId = Number(companyId);
    if (departmentId) filters.departmentId = Number(departmentId);
    if (type) filters.type = type;
    if (situation) filters.situation = situation;
    if (search) filters.search = search;

    setLoading(true);
    setError(null);
    fireEquipmentService
      .getFireEquipments(filters)
      .then((response) => {
        if (!alive) return;
        setRows(response.data);
        setTotal(response.pagination.total);
      })
      .catch((err: unknown) => alive && setError(errorMessage(err, 'Erro ao carregar equipamentos')))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [tipos, page, rowsPerPage, companyId, departmentId, type, situation, search, reload]);

  const retry = () => {
    if (!tipos) setTypesAttempt((attempt) => attempt + 1);
    setReload((count) => count + 1);
  };

  /** Grava filtros na URL (vazio apaga a chave) e volta à primeira página. */
  const setUrlFilters = useCallback(
    (changes: Partial<Record<'situation' | 'type' | 'companyId' | 'departmentId', string>>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
          });
          return next;
        },
        { replace: true }
      );
      setPage(0);
    },
    [setSearchParams]
  );
  const setUrlFilter = (key: 'situation' | 'type', value: string) => setUrlFilters({ [key]: value });

  const clearAll = () => {
    setSearchParams({}, { replace: true });
    setSearch('');
    setPage(0);
  };

  const toggleAttention = (reason: FireSituationReason) => setUrlFilter('situation', situation === reason ? '' : reason);

  const handleRegister = async (equipment: FireEquipment) => {
    setOpening(equipment.id);
    setActionError(null);
    try {
      setInspecting(await fireEquipmentService.getFireEquipmentById(equipment.id));
    } catch (err: unknown) {
      setActionError(errorMessage(err, 'Erro ao abrir o equipamento'));
    } finally {
      setOpening(null);
    }
  };

  const handleReactivate = async (equipment: FireEquipment) => {
    setReactivating(equipment.id);
    setActionError(null);
    try {
      await fireEquipmentService.updateFireEquipment(equipment.id, { status: 'IN_USE' });
      enqueueSnackbar(`${equipment.identifier} voltou a ser usado. Confira as validades.`, { variant: 'success' });
      setReload((count) => count + 1);
    } catch (err: unknown) {
      setActionError(errorMessage(err, 'Erro ao reativar o equipamento'));
    } finally {
      setReactivating(null);
    }
  };

  const columns = tipos
    ? buildColumns(tipos, { opening, reactivating, onRegister: handleRegister, onReactivate: handleReactivate })
    : [];

  const filtersActive = Boolean(situation || type || companyId || departmentId || search);
  const hasAttention = summary !== null && ATTENTION_BUTTONS.some(({ count }) => summary[count] > 0);

  const typeOptions = (tipos?.types ?? []).map((descriptor) => ({ value: descriptor.type, label: descriptor.label }));
  const situationOptions = tipos
    ? SITUATION_FILTERS.map((option) => ({
        value: option,
        label: option === 'FORA_DE_USO' ? tipos.labels.situation.FORA_DE_USO : tipos.labels.reason[option],
      }))
    : [];
  const optionLabel = (options: readonly { value: string; label: string }[], value: string) =>
    options.find((option) => option.value === value)?.label ?? value;

  const chips: ActiveChip[] = [];
  if (companyId) {
    chips.push({
      key: 'companyId',
      label: 'Empresa',
      value: optionLabel(companyList.itens, companyId),
      onDelete: () => setUrlFilters({ companyId: '', departmentId: '' }),
    });
  }
  if (type) {
    chips.push({ key: 'type', label: 'Tipo', value: optionLabel(typeOptions, type), onDelete: () => setUrlFilter('type', '') });
  }
  if (departmentId) {
    chips.push({
      key: 'departmentId',
      label: 'Setor',
      value: optionLabel(departmentList.itens, departmentId),
      onDelete: () => setUrlFilters({ departmentId: '' }),
    });
  }
  if (situation) {
    chips.push({
      key: 'situation',
      label: 'Situação',
      value: optionLabel(situationOptions, situation),
      onDelete: () => setUrlFilter('situation', ''),
    });
  }

  const limparFiltros = {
    label: 'Limpar filtros',
    node: 'fire-equipments',
    deniedHint: 'Limpe os filtros para ver os equipamentos.',
    onClick: clearAll,
    allowed: true,
  };

  // Um objeto com texto literal por situação: o teste dos textos de vazio lê cada description
  // como texto inteiro, então a escolha entre as frases fica no ternário de fora.
  const emptyState =
    filtersActive && summary
      ? {
          title: 'Nenhum equipamento com esses filtros',
          description: `Há ${summary.total} equipamentos cadastrados ${
            shouldShowCompanyField && !companyId ? 'no seu acesso' : 'para esta empresa'
          }, mas nenhum atende ao que você escolheu.`,
          action: limparFiltros,
        }
      : filtersActive
      ? {
          title: 'Nenhum equipamento com esses filtros',
          description: 'Nenhum equipamento atende ao que você escolheu.',
          action: limparFiltros,
        }
      : {
          title: 'Nenhum equipamento de incêndio cadastrado para esta empresa',
          description:
            'O documento da NR 23 lista cada extintor, hidrante e alarme com a validade e a última inspeção. Cadastre o primeiro; depois use "Salvar e cadastrar outro" para os que ficam no mesmo setor. Os avisos de vencimento passam a sair sozinhos.',
          action: {
            label: 'Cadastrar o primeiro equipamento',
            node: 'fire-equipments',
            deniedHint: 'Peça a um administrador para cadastrar os equipamentos de incêndio desta empresa.',
            onClick: () => navigate('/fire-equipments/new'),
            allowed: !criar.disabled,
          },
        };

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Equipamentos de incêndio' }]}
      title="Equipamentos de incêndio"
      subtitle="Extintores, hidrantes e demais equipamentos, com a validade e a última inspeção de cada um."
      actions={
        <RouteActionButton
          para="/fire-equipments/new"
          exige={pode('fire_equipment.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Novo equipamento
        </RouteActionButton>
      }
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" onClick={retry} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {!error && summaryError && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" onClick={() => setReload((count) => count + 1)} sx={{ minHeight: TOUCH_TARGET }}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar a faixa "Precisam de atenção". A lista abaixo continua valendo.
        </Alert>
      )}

      {!error && !summaryError && summary === null && (
        <Skeleton variant="rounded" height={TOUCH_TARGET} sx={{ mb: 3 }} />
      )}

      {!error && summary !== null && summary.total > 0 && tipos && (
        <Box
          role="group"
          aria-labelledby="faixa-atencao"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 3 }}
        >
          <Typography id="faixa-atencao" variant="body1" color="text.secondary" sx={{ mr: 1 }}>
            Precisam de atenção
          </Typography>
          {hasAttention ? (
            ATTENTION_BUTTONS.map(({ reason, count }) => (
              <Button
                key={reason}
                variant={situation === reason ? 'contained' : 'outlined'}
                color="inherit"
                aria-pressed={situation === reason}
                onClick={() => toggleAttention(reason)}
                sx={{ minHeight: TOUCH_TARGET, fontWeight: 600 }}
              >
                {`${tipos.labels.reason[reason]}: ${summary[count]}`}
              </Button>
            ))
          ) : (
            <Typography variant="body1">Nenhum equipamento precisa de atenção agora.</Typography>
          )}
        </Box>
      )}

      {tipos && (
        <Box sx={{ mb: 3 }}>
          <InlineFilterBar
            searchPlaceholder="Identificação ou local"
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(0);
            }}
            resultCount={error ? undefined : total}
            resultNoun="equipamentos"
            chips={chips}
            onClearAll={clearAll}
            loading={loading}
          >
            {shouldShowCompanyField && (
              <Tooltip title={companyList.bloqueio ?? ''}>
                <span>
                  <InlineSelect
                    label="Empresa"
                    value={companyId}
                    options={[...companyList.itens]}
                    onChange={(value) => setUrlFilters({ companyId: value, departmentId: '' })}
                    emptyLabel="Todas as empresas"
                    minWidth={200}
                    disabled={!companyList.permitido}
                  />
                </span>
              </Tooltip>
            )}
            <InlineSelect
              label="Tipo"
              value={type}
              options={typeOptions}
              onChange={(value) => setUrlFilter('type', value)}
              emptyLabel="Todos"
              minWidth={180}
            />
            <Tooltip title={departmentList.bloqueio ?? ''}>
              <span>
                <InlineSelect
                  label="Setor"
                  value={departmentId}
                  options={[...departmentList.itens]}
                  onChange={(value) => setUrlFilters({ departmentId: value })}
                  emptyLabel="Todos"
                  minWidth={180}
                  disabled={!departmentList.permitido}
                />
              </span>
            </Tooltip>
            <InlineSelect
              label="Situação"
              value={situation}
              options={situationOptions}
              onChange={(value) => setUrlFilter('situation', value)}
              emptyLabel="Todas"
              minWidth={200}
            />
          </InlineFilterBar>
        </Box>
      )}

      {!error && (
        <DataTable
          columns={columns}
          rows={rows}
          getRowId={(equipment) => equipment.id}
          loading={loading || tipos === null}
          emptyState={emptyState}
          onRowClick={(equipment) => navigate(`/fire-equipments/${equipment.id}`)}
          pagination={{
            page,
            rowsPerPage,
            totalCount: total,
            onPageChange: setPage,
            onRowsPerPageChange: (value) => {
              setRowsPerPage(value);
              setPage(0);
            },
          }}
        />
      )}

      {inspecting && tipos && (
        <InspectionDialog
          open
          equipment={{
            id: inspecting.id,
            identifier: inspecting.identifier,
            location: inspecting.location,
            summary: equipmentSummary(inspecting, tipos),
            inspectionFrequency: inspecting.inspectionFrequency,
            lastInspectionDate: inspecting.lastInspectionDate,
            inspectionDueFrom: inspecting.inspectionDueFrom,
          }}
          labels={tipos.labels}
          onClose={() => setInspecting(null)}
          onSaved={() => {
            setInspecting(null);
            enqueueSnackbar('Inspeção registrada.', { variant: 'success' });
            setReload((count) => count + 1);
          }}
        />
      )}
    </PageShell>
  );
};

export default FireEquipmentListPage;
